import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, FileText, StickyNote, Mic, Users, Gift, Code, Box } from 'lucide-react';

const ProductShowcase = () => {
  const [activeTab, setActiveTab] = useState('stagex');
  const [qsShareImageIndex, setQsShareImageIndex] = useState(0);
  
  const products = {
    qsshare: {
      name: 'QuickSync Me',
      title: 'Share. Collaborate. No Compromise.',
      titleHighlight: 'No Compromise.',
      description: 'Fastest and simplest way to share and collaborate privately',
      logo: '/quicksync-me-icon.png',
      tags: [
        { icon: FileText, label: 'Files' },
        { icon: StickyNote, label: 'Docs' }
      ],
      images: [
        'https://customer-assets.emergentagent.com/job_buildblocks-28/artifacts/xt1k19ln_Group%2048650.png',
        'https://customer-assets.emergentagent.com/job_1ee40d8b-e47a-4f63-85e0-ce0928dc4eb3/artifacts/rnb3u96i_Group%2048650%20%281%29.png'
      ],
      accentColor: 'from-emerald-500 via-teal-500 to-emerald-500',
      primaryCTA: 'Start Sharing',
      primaryLink: 'https://quicksync.me/',
      secondaryCTA: 'Learn More',
      secondaryLink: 'https://docs.quicksync.me/qs-share'
    },
    stagex: {
      name: 'StageX',
      title: 'Host. Meet. Gather. Your Terms.',
      titleHighlight: 'Your Terms.',
      description: 'Live streams for free speech, meaningful Two-Way talk and InteractionFi',
      logo: 'https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/b6z8aocx_StageX.png',
      tags: [
        { icon: Mic, label: 'Live' },
        { icon: Users, label: 'Social' },
        { icon: Gift, label: 'Rewards' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_collab-hub-62/artifacts/83ldlm6n_StageX%20Interface.png',
      accentColor: 'from-purple-500 via-pink-500 to-purple-500',
      primaryCTA: 'Go Live',
      primaryLink: 'https://stagex.space/',
      secondaryCTA: 'Learn More',
      secondaryLink: 'https://docs.quicksync.me/stagex'
    },
    builders: {
      name: 'QS Framework',
      title: 'Build private apps. Fast.',
      titleHighlight: 'Fast.',
      description: 'The full stack privacy workspaces infrastructure',
      logo: 'https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/wg56q37t_Framework%20QS.png',
      tags: [
        { icon: Code, label: 'API' },
        { icon: Box, label: 'SDK' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_buildblocks-28/artifacts/r5hajaii_image%20149.png',
      accentColor: 'from-emerald-500 via-teal-500 to-emerald-500',
      primaryCTA: 'Start Building',
      primaryLink: 'https://docs.quicksync.me/qs-framework',
      secondaryCTA: 'Learn More',
      secondaryLink: 'https://docs.quicksync.me/qs-framework'
    }
  };

  const currentProduct = products[activeTab];

  // Auto-rotate QuickSync Me images
  useEffect(() => {
    if (activeTab === 'qsshare') {
      const interval = setInterval(() => {
        setQsShareImageIndex((prev) => prev === 0 ? 1 : 0);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const getCurrentImage = () => {
    if (activeTab === 'qsshare' && currentProduct.images) {
      return currentProduct.images[qsShareImageIndex];
    }
    return currentProduct.image;
  };

  const titleParts = currentProduct.title.split(currentProduct.titleHighlight);

  return (
    <section className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Title */}
        <div className="text-center mb-8 md:mb-12">
          <p className="text-xs md:text-sm text-gray-500 uppercase tracking-wider">Discover Your Private Spaces</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-10 md:mb-16">
          <div className="flex justify-center gap-6 sm:gap-8 md:gap-12">
            <button
              onClick={() => setActiveTab('stagex')}
              className={`pb-3 md:pb-4 px-1 md:px-2 text-sm md:text-base font-medium transition-all duration-300 border-b-2 whitespace-nowrap ${
                activeTab === 'stagex'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              StageX
            </button>
            <button
              onClick={() => setActiveTab('qsshare')}
              className={`pb-3 md:pb-4 px-1 md:px-2 text-sm md:text-base font-medium transition-all duration-300 border-b-2 whitespace-nowrap ${
                activeTab === 'qsshare'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              QuickSync Me
            </button>
            <button
              onClick={() => setActiveTab('builders')}
              className={`pb-3 md:pb-4 px-1 md:px-2 text-sm md:text-base font-medium transition-all duration-300 border-b-2 whitespace-nowrap ${
                activeTab === 'builders'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Builders
            </button>
          </div>
        </div>

        {/* Two Column Layout - Same as Feature Sections */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            {/* Main Title */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight">
              {titleParts[0]}
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                {currentProduct.titleHighlight}
              </span>
              {titleParts[1]}
            </h2>

            {/* Logo Box with Name and Feature Tags */}
            <div className="flex items-center gap-6 flex-wrap">
              {/* Logo and Name */}
              <div className="flex items-center gap-2">
                <div className="w-20 h-20 bg-white rounded-xl p-3 flex items-center justify-center shadow-md border-2 border-gray-200">
                  <img 
                    src={currentProduct.logo}
                    alt={currentProduct.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-2xl font-bold text-gray-900">{currentProduct.name}</span>
              </div>

              {/* Feature Tag Boxes */}
              <div className="flex items-center gap-3">
                {currentProduct.tags.map((tag, index) => {
                  const Icon = tag.icon;
                  return (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer">
                      <div className="flex flex-col items-center gap-1.5">
                        <Icon className="w-4 h-4 text-gray-700" strokeWidth={2} />
                        <span className="text-xs font-medium text-gray-700">{tag.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-2xl">
              {currentProduct.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 pt-2">
              <a
                href={currentProduct.primaryLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-6 py-3 text-base font-medium transition-all duration-300 hover:shadow-lg group inline-flex items-center"
                >
                  {currentProduct.primaryCTA}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a
                href={currentProduct.secondaryLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 rounded-lg px-6 py-3 text-base font-medium transition-all duration-300 hover:shadow-md group inline-flex items-center"
                >
                  {currentProduct.secondaryCTA}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>

          {/* Right Side - Floating Canvas */}
          <div className="relative perspective-1000">
            <div className="relative max-w-4xl mx-auto">
              {/* Main Floating Canvas */}
              <div className="relative transform hover:scale-[1.02] hover:-translate-y-2 transition-all duration-700">
                {/* Multiple Shadow Layers */}
                <div className="absolute inset-0 bg-gray-900/5 rounded-3xl blur-2xl translate-y-8 scale-95"></div>
                <div className="absolute inset-0 bg-gray-900/10 rounded-3xl blur-xl translate-y-4 scale-98"></div>
                
                {/* Main Container */}
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden transform rotate-2 hover:rotate-0 transition-transform duration-700">
                  {/* Top Accent Bar */}
                  <div className={`h-1 bg-gradient-to-r ${currentProduct.accentColor}`}></div>
                  
                  {/* Interface */}
                  <div className="relative p-1">
                    <img 
                      src={getCurrentImage()}
                      alt={`${currentProduct.name} Interface`}
                      className="w-full h-auto rounded-2xl"
                    />
                    <div className="absolute inset-0 rounded-2xl shadow-inner pointer-events-none"></div>
                  </div>
                </div>
              </div>

              {/* Floating Feature Badges */}
              {activeTab === 'qsshare' && (
                <>
                  <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Short lived</span>
                    </div>
                  </div>
                  <div className="absolute -right-6 bottom-1/3 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-delayed z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Real-time sync</span>
                    </div>
                  </div>
                  <div className="absolute -left-6 bottom-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-slow z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">No sign-up</span>
                    </div>
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl px-5 py-3 shadow-2xl border border-gray-700 hover:scale-105 transition-transform duration-300 z-20">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                        <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping"></div>
                      </div>
                      <span className="text-sm font-semibold text-white whitespace-nowrap">End-to-End Encrypted</span>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'stagex' && (
                <>
                  <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Anonymous</span>
                    </div>
                  </div>
                  <div className="absolute -right-6 bottom-1/3 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-delayed z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Live token buys</span>
                    </div>
                  </div>
                  <div className="absolute -left-6 bottom-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-slow z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Community Votes</span>
                    </div>
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl px-5 py-3 shadow-2xl border border-gray-700 hover:scale-105 transition-transform duration-300 z-20">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
                        <div className="absolute inset-0 w-3 h-3 rounded-full bg-purple-500 animate-ping"></div>
                      </div>
                      <span className="text-sm font-semibold text-white whitespace-nowrap">Live Streaming</span>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'builders' && (
                <>
                  <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">REST APIs</span>
                    </div>
                  </div>
                  <div className="absolute -right-6 bottom-1/3 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-delayed z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">WebSocket Support</span>
                    </div>
                  </div>
                  <div className="absolute -left-6 bottom-1/4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-200 hover:scale-110 transition-transform duration-300 animate-float-slow z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">SDK Libraries</span>
                    </div>
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl px-5 py-3 shadow-2xl border border-gray-700 hover:scale-105 transition-transform duration-300 z-20">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                        <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-500 animate-ping"></div>
                      </div>
                      <span className="text-sm font-semibold text-white whitespace-nowrap">Developer Ready</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
